import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { MessageSendFilter, cadenceApi, messageSendsApi, templateApi } from '../services/api';
import { Cadence, MessageSend, SendStatsRow, WhatsAppTemplate } from '../types';
import { SEND_PILL, errorLabel, istDayBounds, istToday, pct, shiftDay } from '../lib';
import DateRangeBar from '../components/DateRangeBar';
import NumberStrip, { StripItem } from '../components/NumberStrip';
import { Section, Empty, Failed, LeadCell, Loading, PageFrame, Strip, Tabs, Th, tr } from '../components/PlatformBits';
import { DeliveryCell, Pager, ReplyCell, VisitCell, useReplies } from '../components/SendCells';
import CadenceBoard from '../components/CadenceBoard';

/** One cadence's page: its all-time numbers, the delivery board (every lead
 *  in it, a dot per step), then the steps that went out in the range, newest
 *  first. Numbers from the ledger; a held enrolment is not a send, so "held
 *  now" comes from the cadence itself. */

type Segment = '' | 'replied' | 'visited' | 'failed';
const PAGE = 25;

const CadenceDetailPage: React.FC = () => {
  const { cadenceId } = useParams<{ cadenceId: string }>();
  const [params, setParams] = useSearchParams();
  const today = istToday();
  const from = params.get('from') || shiftDay(today, -6);
  const to = params.get('to') || today;
  const [cadences, setCadences] = useState<Cadence[] | null>(null);
  const [templates, setTemplates] = useState<WhatsAppTemplate[]>([]);
  const [byStep, setByStep] = useState<SendStatsRow[] | null>(null);
  const [page, setPage] = useState<{ total: number; data: MessageSend[] } | null>(null);
  const [segment, setSegment] = useState<Segment>('');
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const bounds = useMemo(() => istDayBounds(from, to), [from, to]);

  useEffect(() => {
    Promise.all([cadenceApi.list(), templateApi.list()])
      .then(([c, t]) => { setCadences(c); setTemplates(t); })
      .catch((err) => setError(err?.response?.data?.detail || err.message));
  }, []);

  useEffect(() => {
    if (!cadenceId) return;
    messageSendsApi.stats('step', { producer: 'cadence', cadence_id: cadenceId })
      .then(setByStep).catch((err) => setError(err?.response?.data?.detail || err.message));
  }, [cadenceId]);

  useEffect(() => {
    if (!cadenceId) return;
    const filter: MessageSendFilter = { producer: 'cadence', cadence_id: cadenceId, ...bounds };
    if (segment) filter.segment = segment;
    setLoading(true);
    messageSendsApi.list(filter, skip, PAGE)
      .then((p) => { setPage(p); setLoading(false); })
      .catch((err) => setError(err?.response?.data?.detail || err.message));
  }, [bounds, cadenceId, segment, skip]);

  const stepById = useMemo(() => {
    const out: Record<string, { cadence: Cadence; position: number; offset: string }> = {};
    for (const c of cadences || []) for (const st of c.steps) {
      out[st.id] = { cadence: c, position: st.position, offset: `T+${st.offset_days}d${st.at_time ? ` at ${st.at_time.slice(0, 5)}` : st.offset_hours ? ` ${st.offset_hours}h` : ''}` };
    }
    return out;
  }, [cadences]);
  const templateById = useMemo(() => Object.fromEntries(templates.map((t) => [t.id, t])), [templates]);

  const rows = page?.data || [];
  const replies = useReplies(page?.data ?? null);

  const totals = useMemo(() => {
    const sum = { sends: 0, delivered: 0, read: 0, replied: 0, visited: 0, failed: 0 };
    for (const r of byStep || []) {
      sum.sends += r.sends; sum.delivered += r.delivered; sum.read += r.read; sum.replied += r.replied; sum.visited += r.visited; sum.failed += r.failed;
    }
    return sum;
  }, [byStep]);
  const cadence = useMemo(() => cadences?.find((c) => c.id === cadenceId) || null, [cadences, cadenceId]);

  if (error) return <Failed what="cadence" error={error} />;
  if (!cadences) return <div className="w-full px-10 pt-8"><Loading what="cadence" /></div>;
  if (!cadence) return <Failed what="cadence" error="no cadence with this id" />;

  const strip: StripItem[] = [
    { v: totals.visited, k: 'visits booked', cls: 'visited' },
    { v: totals.replied, k: 'replied', p: pct(totals.replied, totals.sends), cls: 'gold' },
    { v: totals.sends.toLocaleString('en-IN'), k: 'steps sent' },
    { v: totals.delivered.toLocaleString('en-IN'), k: 'delivered', p: pct(totals.delivered, totals.sends) },
    { v: totals.read.toLocaleString('en-IN'), k: 'read', p: pct(totals.read, totals.sends) },
    { v: cadence.enrolled.toLocaleString('en-IN'), k: 'in it now' },
    { v: cadence.held, k: 'held now', cls: cadence.held ? 'gold' : undefined },
    { v: totals.failed, k: 'failed', cls: totals.failed ? 'bad' : undefined },
  ];

  const segments: [Segment, React.ReactNode][] = [
    ['', <>Everyone</>],
    ['replied', <>Replied <span className="num opacity-80">{totals.replied}</span></>],
    ['visited', <>Visited <span className="num opacity-80">{totals.visited}</span></>],
    ['failed', <>Failed <span className="num opacity-80">{totals.failed}</span></>],
  ];

  return (
    <PageFrame title={cadence.name} back={{ to: '/cadences', label: 'Cadences' }}
               subline={`${cadence.steps.length} step${cadence.steps.length === 1 ? '' : 's'} · numbers are all time`}>
      <Strip>{byStep ? <NumberStrip items={strip} /> : <Loading what="numbers" />}</Strip>

      <CadenceBoard cadence={cadence} templateById={templateById} />

      <Section title="Steps sent" note="newest first · what each led to">
        <div className="flex items-center gap-2 flex-wrap px-1 py-3">
          <DateRangeBar from={from} to={to} onChange={(f, t) => { setParams({ from: f, to: t }); setSkip(0); }} caption="steps that went out in the range · IST days" />
          <span className="flex-1" />
          <Tabs value={segment} onChange={(sg) => { setSegment(sg); setSkip(0); }} items={segments} />
        </div>
        <table className="w-full min-w-[880px] border-collapse">
          <thead><tr>
            <Th left>Lead</Th><Th left>Cadence · step</Th><Th left>Template</Th><Th left>Delivery</Th><Th left ours>Site visit</Th><Th left>Their reply</Th>
          </tr></thead>
          <tbody className={`transition-opacity ${loading && page ? 'opacity-50' : ''}`}>
            {!page && <Empty cols={6}>Loading sends…</Empty>}
            {page && rows.length === 0 && <Empty cols={6}>No cadence sends in this range.</Empty>}
            {rows.map((s) => {
              const st = stepById[s.producer_ref.split(':')[2] || ''];
              const t = s.template_id ? templateById[s.template_id] : undefined;
              return (
                <tr key={s.id} className={`${tr} hover:bg-charcoal/[0.025]`}>
                  <LeadCell id={s.lead_id} name={s.lead_name} sub={<span className="num">{s.lead_phone || '—'}</span>} />
                  <td className="px-3 py-3 whitespace-nowrap">
                    {st
                      ? <><span className="font-semibold text-charcoal">{st.cadence.name}</span><div className="text-[11.5px] text-graphite mt-0.5 num">step {st.position + 1} · {st.offset}</div></>
                      : <span className="text-graphite text-[12.5px]">cadence removed since<div className="text-[11px] num">step {s.producer_ref.split(':')[2]?.slice(0, 8)}</div></span>}
                  </td>
                  <td className="px-3 py-3 max-w-[220px]">
                    {t
                      ? <Link to={`/templates/${t.id}`} className="block truncate text-charcoal hover:text-forest hover:underline underline-offset-2" title={t.display_name}>{t.display_name}</Link>
                      : <span className="text-graphite text-[12.5px]">{s.template_id || 'free text'}</span>}
                  </td>
                  <DeliveryCell status={s.status} pillCls={SEND_PILL[s.status]} sentAt={s.sent_at} readAt={s.read_at}
                                failure={s.status === 'failed' ? (s.error_code ? errorLabel(s.error_code) : s.error_detail || 'failed') : null}
                                freeform={s.delivered_as === 'freeform' && s.status !== 'failed'} />
                  <VisitCell visitedAt={s.visited_at} status={s.visit_task_status} />
                  <ReplyCell repliedAt={s.replied_at} rows={s.replied_at ? replies[s.id] : undefined} />
                </tr>
              );
            })}
          </tbody>
        </table>
        {page && <Pager skip={skip} limit={PAGE} total={page.total} onSkip={setSkip} />}
      </Section>
    </PageFrame>
  );
};

export default CadenceDetailPage;
